/**
 * Internal User Redirect Hook
 *
 * Redirects internal users (@shi.com, @stratascale.com) to Customer Success
 * unless they are on an /internal/ or /attachment/ route, impersonating a
 * customer, or have the CX bypass cookie set.
 *
 * Gated by `nomads.enable-unified-login` feature flag.
 */
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { isShiUserEmail } from "shared-ui/src/functions/shiUserCheck";
import {
  isImpersonatingAccount,
  isAssumingUserIdentity,
  isImpersonatingUser,
} from "shared-ui/src/utils/clientmanagement";
import { useFeatureFlag } from "shared-ui/src/utils/FeatureFlagHelper";
import { hasCxBypassCookie } from "shared-ui/src/utils/cxBypassCookie";
import { useClaims } from "./ClaimsProvider";
import { AppendIfPrefixExists } from "../modules/sitePaths";

/**
 * Hook that checks if authenticated user should be redirected to CS.
 * Should be called early in the app lifecycle (e.g., in App component).
 * Re-checks on every route change to catch React Router navigation.
 */
export function useInternalUserRedirect(): void {
  const { isAuthenticated, isLoading, user } = useAuth0();
  const location = useLocation();
  const navigate = useNavigate();
  const { claims, getClaimValue } = useClaims();
  const { enabled: flagEnabled, loading: flagLoading } = useFeatureFlag(
    "nomads.enable-unified-login",
    false,
  );

  const hasShiClaimsValue = getClaimValue("hasShiClaims")?.toLowerCase();
  const hasShiClaims = hasShiClaimsValue === "true" || hasShiClaimsValue === "1";

  useEffect(() => {
    if (isLoading || flagLoading || !isAuthenticated || !user?.email) return;
    if (!flagEnabled) return;
    if (!isShiUserEmail(user.email)) return;
    if (location.pathname.startsWith("/internal/")) return;
    if (location.pathname.startsWith("/attachment/")) return;
    if (location.pathname.includes("/noprofile")) return;
    if (location.pathname.includes("/register")) return;
    if (
      isImpersonatingAccount() ||
      isImpersonatingUser() ||
      isAssumingUserIdentity()
    )
      return;
    if (hasCxBypassCookie()) return;

    // Only redirect when using Caddy (local.one.shi.com or *.one.shi.com)
    // Skip on localhost to allow running CX standalone for development
    if (
      window.location.hostname === "localhost" ||
      window.location.hostname === "dev.one.shi.com"
    )
      return;
    // Wait for claims to load before making redirect decision
    if (claims.length === 0) return;

    // If user has not completed profile/claims bootstrap, keep them in CX register flow.
    if (!hasShiClaims) {
      navigate(AppendIfPrefixExists("/register"), { replace: true });
      return;
    }

    // Brief delay to let auth state settle, preventing MSAL race conditions in CS
    const csUrl = import.meta.env.CUSTOMER_SUCCESS_UI;
    if (csUrl) {
      setTimeout(() => {
        window.location.href = csUrl;
      }, 100);
    }
  }, [
    isAuthenticated,
    isLoading,
    user,
    location.pathname,
    flagEnabled,
    flagLoading,
    claims,
    hasShiClaims,
    navigate,
    getClaimValue,
  ]);
}
